import _ from 'lodash'
import * as THREE from 'three'
import { round } from 'mathjs'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import DatumLink from '@glass-designer/model/datumLink'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '../types'

/**
 * Datum utility helper functions
 * Used to calculate & generate datum links for objects within a shape
 */
export default class EditorDatumUtils {


  /**
   * Calculate the closest datum links for a center cutout hole relative to the shape / other holes
   * Fills in the datum links array
   * 
   * Options include:
   *    ignoreHoleId - If set, ignores a specific center cutout hole given an id
   *    ignoreAllHoles - If set, ignores all center cutout holes
   *    forceShapeBounds - If set, forces the datum links from the shape bounds
   * 
   * Returns true if the datum links intersect at the required position
   */
  static calculateClosestDatumLinks(shape, datumLinks, referencePosition, options = {}) {
    let resultMatchTolerance = 1.0      // How close to the reference position the datum should resolve to

    if(datumLinks.length < 2) {
      // Fill with default datum links
      while(datumLinks.length < 2) {
        datumLinks.push(new DatumLink())
      }
    }

    // Calculate in 2D space
    let refPoint = new THREE.Vector2(referencePosition.x, referencePosition.y)

    // Store a list of objects from closest to furthest
    // Go through & find 2 objects that would generate an intersection
    // If intersection is outside of the shape, ignore it
    var nearObjectList = []

    // Circle/Oval shape have different rules for datum links
    let isShapeCircle = shape.getShapeType() == GlassDesignerTypes.LibraryItemShapeTypes.circle
    let isShapeOval = shape.getShapeType() == GlassDesignerTypes.LibraryItemShapeTypes.oval

    // Segments
    shape.segmentList.forEach((segment, segmentIndex) => {
      // Ignore segments for circle/ovals
      if(isShapeCircle || isShapeOval)
        return

      let closestPoint = segment.projectPointOnSegment(refPoint, false)

      // Ignore if point is outside of the line
      // Don't test for arcs currently
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        if(!MathsUtils.point2DOnLineSegment(segment.startPosition, segment.endPosition, closestPoint, 1.0))
          return
      }

      let distToSegment = closestPoint.distanceTo(refPoint)

      // Priority system, so tiny segments are not considered over large 
      let priority = 5

      // Small segments have a lower priority unless right next to it
      if(segment.getLength() < 100 && distToSegment > 100) {
        priority = 1
      }

      nearObjectList.push({
        sourceDatumType: GlassDesignerTypes.DatumTypes.glassSegment,
        segmentIndex: segmentIndex,
        distance: distToSegment,
        closestPoint: closestPoint,
        priority: priority,
        isStandAloneDatum: false
      })
    })

    // Oval / Circle Shape : Allow datum from bounds of the oval or circle
    if(isShapeOval || isShapeCircle || options.forceShapeBounds) {
      let bounds = shape.calculateSegmentBounds()

      // 4 edges: top, right, bottom, left
      let edges = [
        [new THREE.Vector2(bounds.min.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.min.y)],
        [new THREE.Vector2(bounds.max.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.max.y)],
        [new THREE.Vector2(bounds.max.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.max.y)],
        [new THREE.Vector2(bounds.min.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.min.y)]
      ]

      // If forced to shape bounds, reset near object list
      if(options.forceShapeBounds) {
        nearObjectList = []
      }

      edges.forEach((edge, edgeIndex) => {
        let closestPoint = MathsUtils.getClosest2DPointOnLineSegment(edge[0], edge[1], refPoint, false)

        // Ignore if point is outside of the line
        if(!MathsUtils.point2DOnLineSegment(edge[0], edge[1], closestPoint, 1.0))
          return

        nearObjectList.push({
          sourceDatumType: GlassDesignerTypes.DatumTypes.shapeBoundsEdge,
          shapeBoundsEdgeIndex: edgeIndex,
          distance: closestPoint.distanceTo(refPoint),
          closestPoint: closestPoint,
          priority: 5,
          isStandAloneDatum: false
        })
      })
    }

    // Datum lines
    shape.getDatumLineList().forEach(datumLine => {
      if(options.forceShapeBounds)
        return

      let closestPoint = datumLine.projectPointOnDatumLine(refPoint, false)

      // Ignore if point is outside of the datum line
      if(!MathsUtils.point2DOnLineSegment(datumLine.startPosition, datumLine.endPosition, closestPoint, 1.0))
        return

      let dist = closestPoint.distanceTo(refPoint)

      nearObjectList.push({
        sourceDatumType: GlassDesignerTypes.DatumTypes.datumLine,
        datumLineId: datumLine.id,
        distance: dist,
        closestPoint: closestPoint,
        priority: 5,
        isStandAloneDatum: false
      })

      // Add start/end points of the datum line
      // Allows us to set dimensions along a datum line
      closestPoint = MathsUtils.project2DPointOnPlane(refPoint, datumLine.getDirection(), datumLine.startPosition)
      nearObjectList.push({
        sourceDatumType: GlassDesignerTypes.DatumTypes.datumLineStart,
        datumLineId: datumLine.id,
        distance: closestPoint.distanceTo(refPoint),
        closestPoint: closestPoint,
        priority: 5,
        isStandAloneDatum: false
      })

      closestPoint = MathsUtils.project2DPointOnPlane(refPoint, datumLine.getDirection().negate(), datumLine.endPosition)
      nearObjectList.push({
        sourceDatumType: GlassDesignerTypes.DatumTypes.datumLineEnd,
        datumLineId: datumLine.id,
        distance: closestPoint.distanceTo(refPoint),
        closestPoint: closestPoint,
        priority: 5,
        isStandAloneDatum: false
      })
    })

    // Center Cutouts / Holes
    shape.getCenterCutoutList().forEach(hole => {
      if(hole.id == options.ignoreHoleId || !!options.ignoreAllHoles || options.forceShapeBounds)
        return

      let bounds = hole.calculateSegmentBounds()

      // Ignore if the point is not aligned with the box on horizontal/vertical axis
      // Ignores rotation on holes
      let insideHorz = bounds.min.x <= refPoint.x && bounds.max.x >= refPoint.x
      let insideVert = bounds.min.y <= refPoint.y && bounds.max.y >= refPoint.y
      if(!insideHorz && !insideVert)
        return
      
      var closestPoint = hole.worldPoint.clone()
      var dist = closestPoint.distanceTo(refPoint)

      let srcAxisType = insideVert ? GlassDesignerTypes.AxisTypes.horizontal : GlassDesignerTypes.AxisTypes.vertical
      if(srcAxisType == GlassDesignerTypes.AxisTypes.horizontal)
        dist = Math.abs(closestPoint.x - refPoint.x)
      else
        dist = Math.abs(closestPoint.y - refPoint.y)

      nearObjectList.push({
        sourceDatumType: GlassDesignerTypes.DatumTypes.hole,
        holeId: hole.id,
        distance: dist,
        closestPoint: closestPoint,
        isVertical: srcAxisType == GlassDesignerTypes.AxisTypes.vertical,
        isHorizontal: srcAxisType == GlassDesignerTypes.AxisTypes.horizontal,
        sourceHoleMeasurePointHorzType: hole.measureToHorizontal,
        sourceHoleMeasurePointVertType: hole.measureToVertical,
        sourceHoleMeasureAxisType: srcAxisType,
        priority: 5,
        isStandAloneDatum: false
      })
    })

    // Sort by priority, then closest first
    nearObjectList.sort((objA, objB) => {
      if(objA.priority != objB.priority)
        return objB.priority - objA.priority
      return objA.distance - objB.distance
    })

    // Find nearest 2 reference objects to datum to
    // The 2 reference objects cast direction need to intersect
    // Note: Some datum links (eg, circle edge) only require 1 datum link to successfully resolve a position

    let hasStandAloneDatums = nearObjectList.filter(obj => obj.isStandAloneDatum).length > 0
    let standardDatumCount = nearObjectList.filter(obj => !obj.isStandAloneDatum).length

    // Can't do anything with only 1 nearby object
    if(!hasStandAloneDatums && standardDatumCount < 2)
      throw new Error('No valid objects nearby for datum calculations')

    var validDatumSolutionFound = false
    for(var i=0; i<nearObjectList.length && !validDatumSolutionFound; i++) {
      // Stand alone datums
      if(nearObjectList[i].isStandAloneDatum) {
        datumLinks[0].clear()
        datumLinks[1].clear()

        EditorDatumUtils.setDatumLinkFromNearObjectItem(datumLinks[0], shape, refPoint, nearObjectList[i])

        // Calculate if a valid intersection
        // If so, stop calculations and use the 2 datum links
        if(EditorDatumUtils.calculateDatumLinksWorldPoint(shape.editor, datumLinks)) {
          validDatumSolutionFound = true

          // Only need the 1st datum link, remove the 2nd datum link
          datumLinks.splice(1, 1)
          break
        }
      }

      for(var j=i+1; j<nearObjectList.length && !validDatumSolutionFound; j++) {
        // Ignore matching with stand alone datums
        if(nearObjectList[j].isStandAloneDatum)
          continue

        datumLinks[0].clear()
        datumLinks[1].clear()

        EditorDatumUtils.setDatumLinkFromNearObjectItem(datumLinks[0], shape, refPoint, nearObjectList[i])
        EditorDatumUtils.setDatumLinkFromNearObjectItem(datumLinks[1], shape, refPoint, nearObjectList[j])

        // Calculate if a valid intersection & results in required position
        // If so, stop calculations and use the 2 datum links
        let resultWorldPoint = EditorDatumUtils.calculateDatumLinksWorldPoint(shape.editor, datumLinks)
        if(resultWorldPoint && resultWorldPoint.distanceTo(referencePosition) < resultMatchTolerance) {
          validDatumSolutionFound = true
        }
      }
    }

    return validDatumSolutionFound
  }


  /**
   * Setup a DatumLink object from an object about a nearby item in the shape
   * Objects are calculated via calculateClosestDatumLinks()
   */
  static setDatumLinkFromNearObjectItem(datumLink, shape, refPoint, nearObjectInfo) {
    switch(nearObjectInfo.sourceDatumType) {
      case GlassDesignerTypes.DatumTypes.glassSegment: {
        let segment = shape.getSegmentForIndex(nearObjectInfo.segmentIndex)
        if(!segment)
          return

        datumLink.update({
          sourceShapeId: shape.id,
          sourceDatumType: nearObjectInfo.sourceDatumType,
          sourceOffsetDistance: Math.abs(Math.round(segment.calculateProjectDistanceToSegmentPlane(refPoint))),
          sourceOffsetDirection: Math.round(segment.calculateProjectDistanceToSegmentPlane(refPoint)) >= 0 ? 1 : -1,
          segmentId: segment.id,
          segmentSegmentDistanceAlong: MathsUtils.calculateDistanceToPlane2D(segment.getDirection(), segment.startPosition, nearObjectInfo.closestPoint)
        })
        break
      }

      case GlassDesignerTypes.DatumTypes.shapeCircleEdge: {
        datumLink.update({
          sourceShapeId: shape.id,
          sourceDatumType: nearObjectInfo.sourceDatumType,
          sourceOffsetDistance: Math.round(nearObjectInfo.distance),
          sourceOffsetDirection: 1,
          sourceAngleOffset: nearObjectInfo.sourceAngleOffset
        })
        break
      }

      case GlassDesignerTypes.DatumTypes.shapeBoundsEdge: {
        datumLink.update({
          sourceShapeId: shape.id,
          sourceDatumType: nearObjectInfo.sourceDatumType,
          sourceOffsetDistance: Math.round(nearObjectInfo.distance),
          sourceOffsetDirection: 1,
          shapeBoundsEdgeIndex: nearObjectInfo.shapeBoundsEdgeIndex
        })
        break
      }

      case GlassDesignerTypes.DatumTypes.datumLine: {
        let datumLine = shape.getDatumLineById(nearObjectInfo.datumLineId)
        if(!datumLine)
          return

        let distanceToDatum = MathsUtils.calculateDistanceToPlane2D(datumLine.getOutDirection(), datumLine.startPosition, refPoint)

        datumLink.update({
          sourceShapeId: shape.id,
          sourceDatumType: nearObjectInfo.sourceDatumType,
          sourceOffsetDistance: Math.abs(Math.round(distanceToDatum)),
          sourceOffsetDirection: Math.round(distanceToDatum) >= 0 ? 1 : -1,
          datumLineId: datumLine.id
        })
        break
      }

      case GlassDesignerTypes.DatumTypes.datumLineStart:
      case GlassDesignerTypes.DatumTypes.datumLineEnd:
      {
        let datumLine = shape.getDatumLineById(nearObjectInfo.datumLineId)
        if(!datumLine)
          return

        let distanceToDatum = MathsUtils.calculateDistanceToPlane2D(datumLine.getDirection(), datumLine.startPosition, refPoint)
        if(nearObjectInfo.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineEnd)
          distanceToDatum = MathsUtils.calculateDistanceToPlane2D(datumLine.getDirection().negate(), datumLine.endPosition, refPoint)

        datumLink.update({
          sourceShapeId: shape.id,
          sourceDatumType: nearObjectInfo.sourceDatumType,
          sourceOffsetDistance: Math.abs(Math.round(distanceToDatum)),
          sourceOffsetDirection: Math.round(distanceToDatum) >= 0 ? 1 : -1,
          datumLineId: datumLine.id
        })
        break
      }

      case GlassDesignerTypes.DatumTypes.hole: {
        let hole = shape.getHoleById(nearObjectInfo.holeId)
        if(!hole)
          return

        var offsetDistance = 0
        var offsetDirection = 1
        if(nearObjectInfo.isVertical) {
          offsetDirection = refPoint.y > nearObjectInfo.closestPoint.y ? 1 : -1
          offsetDistance = Math.abs(Math.round(refPoint.y - nearObjectInfo.closestPoint.y))
        }
        else if(nearObjectInfo.isHorizontal) {
          offsetDirection = refPoint.x > nearObjectInfo.closestPoint.x ? 1 : -1
          offsetDistance = Math.abs(Math.round(refPoint.x - nearObjectInfo.closestPoint.x))
        }

        datumLink.update({
          sourceShapeId: shape.id,
          sourceDatumType: nearObjectInfo.sourceDatumType,
          sourceOffsetDistance: offsetDistance,
          sourceOffsetDirection: offsetDirection,
          sourceHoleObjectId: hole.id,
          sourceHoleMeasurePointHorzType: nearObjectInfo.sourceHoleMeasurePointHorzType,
          sourceHoleMeasurePointVertType: nearObjectInfo.sourceHoleMeasurePointVertType,
          sourceHoleMeasureAxisType: nearObjectInfo.sourceHoleMeasureAxisType,
        })
        break
      }

      default:
        console.log(`Unknown datum near object type ${nearObjectInfo.sourceDatumType}`)
        break
    }
  }


  /**
   * Calculate the measure-from-hole horizontal type of a hole's bounds to a reference point
   */
  static calculateSourceHoleHorizontalMeasureType(hole, sourceHoleBounds, refPoint) {
    let tolerance = 0.5
    if(refPoint.x <= sourceHoleBounds.min.x + tolerance)
      return GlassDesignerTypes.HoleMeasurePointTypes.left
    else if(refPoint.x >= sourceHoleBounds.max.x - tolerance)
      return GlassDesignerTypes.HoleMeasurePointTypes.right

    return GlassDesignerTypes.HoleMeasurePointTypes.center    
  }


  /**
   * Calculate the measure-from-hole vertical type of a hole's bounds to a reference point
   */
  static calculateSourceHoleVerticalMeasureType(hole, sourceHoleBounds, refPoint) {
    let tolerance = 0.5
    if(refPoint.y <= sourceHoleBounds.min.y + tolerance)
      return GlassDesignerTypes.HoleMeasurePointTypes.top
    else if(refPoint.y >= sourceHoleBounds.max.y - tolerance)
      return GlassDesignerTypes.HoleMeasurePointTypes.bottom

    return GlassDesignerTypes.HoleMeasurePointTypes.middle  
  }


  /**
   * Calculate the world position that 1 or 2 datum links resolve/intersect at
   * 
   * @param {GlassEditor} editor - GlassEditor object
   * @param {Array<DatumLink>} datumLinkList - Array of datumLinkList to calculate world point for
   * @param {Object} options - Object containing options. Properties include:
   *    validateDatums - If set, will strictly check that datums are valid
   * @return {Vector2} Returns Vector2 object for the world position. Returns null if there is no valid intersection from the datum links
   */
  static calculateDatumLinksWorldPoint(editor, datumLinkList, options = {}) {
    var worldPoint = new THREE.Vector2(0, 0)
    var maxRayCastDistance = 50000

    var intSegList = []
    var standAloneWorldPoints = []

    // Push the world point around based on distance from the linked objects & their direction
    // The first link will set the world point to the origin of the reference object
    datumLinkList.forEach(link => {
      // Datums are invalid until explicitly set
      link.isValidDatumLink = false

      if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegment) {
        let glassShape = editor.getObjectById(link.sourceShapeId)
        if(glassShape == null)
          return

        let segment = glassShape.getSegmentById(link.segmentId)
        if(segment == null)
          return

        let outDir = segment.getOutDirection()
        let dir = segment.getDirection()

        // If outside of the segment range, don't use it
        // Causes issues with shape modifications after center cutouts added & referencing segments that have been split
        // JB - 27/02/2023 - Only do if validateDatums is set. This is due to loading old orders that have invalid datums & lose worldPoint (we now save it)
        if(options.validateDatums) {
          if(link.segmentSegmentDistanceAlong > segment.getLength())
            return
        }

        intSegList.push({
          startPosition: segment.startPosition.clone().addScaledVector(dir, -maxRayCastDistance).addScaledVector(outDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
          endPosition: segment.endPosition.clone().addScaledVector(dir, maxRayCastDistance).addScaledVector(outDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
        })

        link.isValidDatumLink = true
      }

      if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeCircleEdge) {
        let glassShape = editor.getObjectById(link.sourceShapeId)
        if(glassShape == null)
          return

        if(glassShape.getShapeType() != GlassDesignerTypes.LibraryItemShapeTypes.circle)
          return

        // Note: Doesn't use intersections, this datum itself has enough data to calculate the position
        let bounds = glassShape.calculateSegmentBounds()
        let circleRadius = glassShape.radius
        let circleCenter = bounds.getCenter(new THREE.Vector2())
        let dir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(link.sourceAngleOffset))

        // Distance from edge of the circle
        let position = circleCenter.clone().addScaledVector(dir, circleRadius - link.sourceOffsetDistance)
        standAloneWorldPoints.push(position)

        link.isValidDatumLink = true
      }

      if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeBoundsEdge) {
        let glassShape = editor.getObjectById(link.sourceShapeId)
        if(glassShape == null)
          return

        let bounds = glassShape.calculateSegmentBounds()
        // 4 edges: top, right, bottom, left
        let edges = [
          [new THREE.Vector2(bounds.min.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.min.y)],
          [new THREE.Vector2(bounds.max.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.max.y)],
          [new THREE.Vector2(bounds.max.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.max.y)],
          [new THREE.Vector2(bounds.min.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.min.y)]
        ]

        let edgeLine = edges[link.shapeBoundsEdgeIndex]
        var dir = new THREE.Vector2()
        dir.subVectors(edgeLine[1], edgeLine[0])
        dir.normalize()
        let rightDir = new THREE.Vector2(-dir.y, dir.x)

        intSegList.push({
          startPosition: edgeLine[0].clone().addScaledVector(dir, -maxRayCastDistance).addScaledVector(rightDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
          endPosition: edgeLine[1].clone().addScaledVector(dir, maxRayCastDistance).addScaledVector(rightDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
        })

        link.isValidDatumLink = true
      }

      if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLine) {
        let glassShape = editor.getObjectById(link.sourceShapeId)
        if(glassShape == null)
          return

        let datumLine = glassShape.getDatumLineById(link.datumLineId)
        if(datumLine == null)
          return

        let outDir = datumLine.getOutDirection()
        let dir = datumLine.getDirection()

        intSegList.push({
          startPosition: datumLine.startPosition.clone().addScaledVector(dir, -maxRayCastDistance).addScaledVector(outDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
          endPosition: datumLine.endPosition.clone().addScaledVector(dir, maxRayCastDistance).addScaledVector(outDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
        })

        link.isValidDatumLink = true
      }

      if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineStart ||
         link.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineEnd
      ) {
        let glassShape = editor.getObjectById(link.sourceShapeId)
        if(glassShape == null)
          return

        let datumLine = glassShape.getDatumLineById(link.datumLineId)
        if(datumLine == null)
          return

        let pos = link.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineStart ? datumLine.startPosition : datumLine.endPosition
        let outDir = datumLine.getOutDirection()
        let dir = datumLine.getDirection()
        if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineEnd)
          dir.negate()

        intSegList.push({
          startPosition: pos.clone().addScaledVector(outDir, -maxRayCastDistance).addScaledVector(dir, link.sourceOffsetDistance * link.sourceOffsetDirection),
          endPosition: pos.clone().addScaledVector(outDir, maxRayCastDistance).addScaledVector(dir, link.sourceOffsetDistance * link.sourceOffsetDirection),
        })

        link.isValidDatumLink = true
      }

      if(link.sourceDatumType == GlassDesignerTypes.DatumTypes.hole) {
        let glassShape = editor.getObjectById(link.sourceShapeId)
        if(glassShape == null)
          return

        let sourceHole = glassShape.getHoleById(link.sourceHoleObjectId)
        if(sourceHole == null)
          return

        var outDir = new THREE.Vector2(
          link.sourceHoleMeasureAxisType == GlassDesignerTypes.AxisTypes.horizontal ? 1 : 0,
          link.sourceHoleMeasureAxisType == GlassDesignerTypes.AxisTypes.vertical ? 1 : 0
        )
        let dir = new THREE.Vector2(outDir.y, -outDir.x)

        let sourceBounds = sourceHole.calculateSegmentBounds()
        let offsetPoint = MathsUtils.calculateHoleOffsetPoint(sourceBounds, link.sourceHoleMeasurePointHorzType, link.sourceHoleMeasurePointVertType)
        var startPoint = new THREE.Vector2(sourceBounds.min.x + offsetPoint.x, sourceBounds.min.y + offsetPoint.y)

        intSegList.push({
          startPosition: startPoint.clone().addScaledVector(dir, -maxRayCastDistance).addScaledVector(outDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
          endPosition: startPoint.clone().addScaledVector(dir, maxRayCastDistance).addScaledVector(outDir, link.sourceOffsetDistance * link.sourceOffsetDirection),
        })

        link.isValidDatumLink = true
      }
    })

    // Check stand alone datum links first
    if(standAloneWorldPoints.length > 0) {
      return standAloneWorldPoints[0]
    }

    
    // Check for an intersection between the 2 segments
    // Needs to be at least 5 degrees angle different in segments
    // Parallel lines will not work well at all
    if(intSegList.length >= 2) {
      let minAngleRequired = 5.0
      let angle1 = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(intSegList[0].startPosition.clone().sub(intSegList[0].endPosition))))
      let angle2 = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(intSegList[1].startPosition.clone().sub(intSegList[1].endPosition))))
      let diff = MathsUtils.calculateShortestAngleDistanceDegrees(angle1, angle2)

      if(Math.abs(diff) < minAngleRequired || Math.abs(diff - 180) < minAngleRequired)
        return null

      if(!MathsUtils.checkSegmentSegmentIntersection(
        intSegList[0].startPosition,
        intSegList[0].endPosition,
        intSegList[1].startPosition,
        intSegList[1].endPosition,
        worldPoint
      )) {
        return null
      }

      return worldPoint
    }
    
    return null
  }


  /**
   * Attempts to deserialize datum links of objects within a shape (eg, holes, datum lines, etc) and
   * resolve the source linkages using maps of the updated IDs for segments, holes, shapes, etc
   * 
   * Returns an array of DatumLink objects that successfully could be resolved
   */
  static deserializeDatumLinks(shape, segmentIdMap, datumLinkDataList) {
    return _.compact(datumLinkDataList.map(datumData => {
      // Only supports links to segments in the shape currently
      if(datumData.sourceDatumType != GlassDesignerTypes.DatumTypes.glassSegment &&
         datumData.sourceDatumType != GlassDesignerTypes.DatumTypes.glassSegmentStart &&
         datumData.sourceDatumType != GlassDesignerTypes.DatumTypes.glassSegmentEnd)
        return null

      // Check if the link successfully maps to the new segment (or existing segment if mapping not provided)
      let srcSegment = null
      if(segmentIdMap)
        srcSegment = shape.getSegmentById(segmentIdMap[datumData.segmentId])
      else
        srcSegment = shape.getSegmentById(datumData.segmentId)

      if(!srcSegment)
        return null

      let outputDatum = new DatumLink()
      datumData.id = null
      outputDatum.deserialize(datumData)

      outputDatum.segmentId = srcSegment.id
      outputDatum.sourceShapeId = shape.id

      return outputDatum
    }))
  }


}